import React from "react";
import { ArrowRight } from "lucide-react";
import { useT } from "@/lib/LanguageContext";
// Relative on purpose: pure modules, resolvable under Jest without a shim.
import { tenantHref } from "../../lib/tenantPath";
import { PATHWAYS_LEARNER as IDS } from "../../constants/testIds/pathwaysLearner";
import GatedLink from "./GatedLink";

const PRIMARY = "inline-flex h-11 w-full items-center justify-center gap-2 rounded-full bg-eh-ink px-6 text-sm font-semibold text-eh-cream hover:bg-eh-ink/90";
const PRIMARY_DISABLED = "inline-flex h-11 w-full cursor-not-allowed items-center justify-center gap-2 rounded-full bg-eh-ink px-6 text-sm font-semibold text-eh-cream opacity-50";

/**
 * A course step's screen (contract §10.0.5, §10.5, §10.7): the course's cover and title,
 * "N of M lessons", and "Open course", which opens the course player on the practice's
 * site (`player_path`, which carries `?pathway=&step=` so the player links back here).
 * Enrolling in the pathway granted the course, so there is no paywall on the way.
 *
 * The step completes on the server once every lesson is done; nothing here completes it.
 * An unpublished course cannot be opened: a sentence replaces the button.
 *
 * `disabled` (the admin preview): "Open course" is shown but not followable.
 *
 * @param {object} props
 * @param {object} props.course   MemberStepScreen.course
 * @param {boolean} props.done    the step is completed
 * @param {boolean} [props.disabled=false]
 */
export default function StepCourse({ course, done, disabled = false }) {
  const t = useT("public");
  return (
    <div data-testid={IDS.course} className="space-y-3">
      <div className="overflow-hidden rounded-2xl bg-eh-surface">
        {course.cover_url && (
          <img
            src={course.cover_url}
            alt=""
            loading="lazy"
            data-testid={IDS.courseCover}
            className="aspect-video w-full object-cover"
          />
        )}
        <div className="px-4 py-3">
          <p className="font-semibold text-eh-ink break-words">{course.title}</p>
          <p data-testid={IDS.courseLessons} className="mt-1 text-sm text-eh-muted tabular-nums">
            {t("pathways.course.lessons", { done: course.lessons_done, total: course.lessons_total })}
          </p>
        </div>
      </div>
      {!course.published ? (
        <p data-testid={IDS.courseUnavailable} className="text-sm text-eh-muted">{t("pathways.course.unavailable")}</p>
      ) : (
        <GatedLink
          href={tenantHref(course.player_path)}
          disabled={disabled}
          testId={IDS.courseOpen}
          className={PRIMARY}
          disabledClassName={PRIMARY_DISABLED}
        >
          {t("pathways.course.open")}
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </GatedLink>
      )}
      {!done && <p className="text-xs text-eh-muted">{t("pathways.course.completesHint")}</p>}
    </div>
  );
}
